import { useState } from "react";
import { useLang } from "../i18n";
import { useBoot } from "../components/Layout";
import { IMG } from "../content/images";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FinalCTA } from "../components/FinalCTA";
import { Turnstile } from "../components/Turnstile";
import { Field, Input, Textarea, Select, Consent, Honeypot, SubmitButton, FormStatus } from "../components/forms";
import { api, apiError } from "../lib/api";
import { MapPin, Briefcase } from "lucide-react";

const Careers = () => {
  const { lang, t, p } = useLang();
  const boot = useBoot();
  const jobs = boot?.jobs || [];
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", location: "", linkedin: "", cover_note: "" });
  const [cv, setCv] = useState(null);
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ ok: false, msg: "" });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = t.forms.required;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = t.forms.invalidEmail;
    if (!form.position.trim()) errs.position = t.forms.required;
    if (!consent) errs.consent = t.forms.consentRequired;
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true); setStatus({ ok: false, msg: "" });
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ ...form, locale: lang, consent, turnstile_token: token, honeypot }));
      if (cv) fd.append("cv", cv);
      const res = await api.post("/public/applications", fd);
      setStatus({ ok: true, msg: res.data.message || t.careers.successMsg });
      setForm({ name: "", email: "", phone: "", position: "", location: "", linkedin: "", cover_note: "" });
      setCv(null); setConsent(false);
    } catch (err) {
      setStatus({ ok: false, msg: apiError(err, t.forms.error) });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="careers-page">
      <Seo title={t.nav.careers} path="carrieres" />
      <PageHero eyebrow="BULA TELECOMS SA" title={t.careers.heading} sub={t.careers.intro}
        image={IMG.developer} crumbs={[{ label: t.nav.careers }]} />

      <section className="bg-[#F5F5F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#101214]">{t.careers.openPositions}</h2></Reveal>
            {jobs.length === 0 ? (
              <p className="mt-6 text-sm text-[#101214]/55 leading-relaxed bg-white border border-dashed border-[#101214]/20 rounded-2xl p-6" data-testid="no-positions-note">
                {t.careers.noPositions}
              </p>
            ) : (
              <ul className="mt-8 space-y-4">
                {jobs.map((j) => (
                  <li key={j.id} className="bg-white border border-[#101214]/10 rounded-2xl p-6 hover:border-[#FF6600]/40 transition-colors" data-testid={`job-${j.id}`}>
                    <h3 className="font-bold text-[#101214]">{lang === "fr" ? j.title_fr : j.title_en}</h3>
                    <p className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#101214]/55">
                      <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-[#FF6600]" />{j.location}</span>
                      {j.contract_type && <span className="inline-flex items-center gap-1"><Briefcase size={12} className="text-[#FF6600]" />{j.contract_type}</span>}
                    </p>
                    <p className="mt-3 text-sm text-[#101214]/60 leading-relaxed line-clamp-3">{lang === "fr" ? j.description_fr : j.description_en}</p>
                    <button onClick={() => setForm({ ...form, position: lang === "fr" ? j.title_fr : j.title_en })} data-testid={`job-apply-${j.id}`}
                      className="mt-4 text-xs font-black text-[#FF6600] uppercase tracking-wider">{t.careers.apply} →</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border border-[#101214]/10 rounded-2xl p-7 sm:p-9 space-y-5" data-testid="application-form">
              <h2 className="text-xl font-bold text-[#101214]">{t.careers.spontaneous}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label={t.careers.fields.name} required error={errors.name} htmlFor="app-name">
                  <Input id="app-name" data-testid="app-name-input" value={form.name} onChange={set("name")} />
                </Field>
                <Field label={t.careers.fields.email} required error={errors.email} htmlFor="app-email">
                  <Input id="app-email" type="email" data-testid="app-email-input" value={form.email} onChange={set("email")} />
                </Field>
                <Field label={t.careers.fields.phone} htmlFor="app-phone">
                  <Input id="app-phone" data-testid="app-phone-input" value={form.phone} onChange={set("phone")} />
                </Field>
                <Field label={t.careers.fields.position} required error={errors.position} htmlFor="app-position">
                  <Input id="app-position" data-testid="app-position-input" value={form.position} onChange={set("position")} />
                </Field>
                <Field label={t.careers.fields.location} htmlFor="app-location">
                  <Input id="app-location" data-testid="app-location-input" value={form.location} onChange={set("location")} />
                </Field>
                <Field label={t.careers.fields.linkedin} htmlFor="app-linkedin">
                  <Input id="app-linkedin" type="url" data-testid="app-linkedin-input" value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/…" />
                </Field>
              </div>
              <Field label={t.careers.fields.cv} htmlFor="app-cv">
                <Input id="app-cv" type="file" accept=".pdf,.doc,.docx" data-testid="app-cv-input" onChange={(e) => setCv(e.target.files?.[0] || null)} />
              </Field>
              <Field label={t.careers.fields.cover} htmlFor="app-cover">
                <Textarea id="app-cover" data-testid="app-cover-input" value={form.cover_note} onChange={set("cover_note")} />
              </Field>
              <Consent checked={consent} onChange={setConsent} error={errors.consent} testid="app-consent" />
              <Turnstile action="careers" onToken={setToken} />
              <p className="text-xs text-[#101214]/45">{t.forms.privacyNote}</p>
              <FormStatus ok={status.ok} msg={status.msg} testid="application-status" />
              <SubmitButton busy={busy} testid="application-submit-button">{t.careers.submit}</SubmitButton>
              <Honeypot value={honeypot} onChange={setHoneypot} />
            </form>
          </Reveal>
        </div>
      </section>
      <FinalCTA />
    </div>
  );
};

export default Careers;
